import { useNavigate } from 'react-router';
import { MessageSquare } from 'lucide-react';
import type { DashboardRunResponse } from '@/lib/api';
import { WorkflowRunCard } from './WorkflowRunCard';

interface WorkflowRunGroupProps {
  parentPlatformId: string | null;
  runs: DashboardRunResponse[];
  isDocker?: boolean;
  onCancel: (runId: string) => void;
  onResume?: (runId: string) => void;
  onAbandon?: (runId: string) => void;
  onDelete?: (runId: string) => void;
  onApprove?: (runId: string) => void;
  onReject?: (runId: string) => void;
}

export function WorkflowRunGroup({
  parentPlatformId,
  runs,
  isDocker,
  onCancel,
  onResume,
  onAbandon,
  onDelete,
  onApprove,
  onReject,
}: WorkflowRunGroupProps): React.ReactElement {
  const navigate = useNavigate();

  return (
    <div className="space-y-3">
      {/* Group header — only shown when there's a shared parent */}
      {parentPlatformId && (
        <div className="flex items-center gap-3 px-1">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-border" />
          <button
            onClick={(): void => {
              navigate(`/chat/${encodeURIComponent(parentPlatformId)}`);
            }}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-medium text-text-secondary transition-colors hover:border-primary/40 hover:text-primary"
          >
            <MessageSquare className="h-3 w-3" />
            {runs.length} run{runs.length !== 1 ? 's' : ''} from this chat
          </button>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent via-border to-border" />
        </div>
      )}

      {/* Cards for this group */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {runs.map(run => (
          <WorkflowRunCard
            key={run.id}
            run={run}
            isDocker={isDocker}
            onCancel={onCancel}
            onResume={onResume}
            onAbandon={onAbandon}
            onDelete={onDelete}
            onApprove={onApprove}
            onReject={onReject}
          />
        ))}
      </div>
    </div>
  );
}
